import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';

import { useShallow } from 'zustand/react/shallow';

import mockImage from '@/assets/space-list-mock.jpg';
import useMultilineEllipsisDetection from '@/shared/lib/hooks/useMultilineEllipsisDetection';
import { ImageResource } from '@/shared/lib/hooks/useResourceImage';
import useGeneratikTableStore from '@/shared/ui/GeneratikTable/generatikTableStore';
import PriceText from '@/shared/ui/PriceText';
import { ResourceImage } from '@/shared/ui/ResourceImage';

import { ListItemName } from './ShoppingCart.styles';
import ShoppingCartSubtitle from './ShoppingCartSubtitle';

const IMAGE_SX = {
  objectFit: 'cover' as const,
  borderRadius: '0.3rem',
  height: '3rem',
  width: '3rem',
};

interface Props {
  rowId: string;
  table: 'ADVERTISING_SPACES' | 'ADVERTISING_PACKAGES';
  imageResource?: ImageResource | null;
  title: string;
  price: number | undefined;
  floor?: string;
  exposureType?: string;
  placementArea?: string;
  onClick: () => void;
}

const ShoppingCartListItem: FC<Props> = ({
  rowId,
  table,
  imageResource,
  title,
  price,
  floor,
  exposureType,
  placementArea,
  onClick,
}) => {
  const { t } = useTranslation();
  const { ref, hasEllipsis } = useMultilineEllipsisDetection();
  const removeSelectedRow = useGeneratikTableStore(useShallow((state) => state.removeSelectedRow));

  const [isHovered, setIsHovered] = useState(false);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        gap: '0.5rem',
        padding: '0.375rem 0',
        borderRadius: '0.25rem',
        backgroundColor: isHovered ? 'purple.50' : 'transparent',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ gap: '0.5rem', cursor: 'pointer' }}
        onClick={onClick}
      >
        <ResourceImage
          resource={imageResource ?? null}
          alt={title}
          size={{ tier: 'thumb' }}
          fallback={mockImage}
          sx={IMAGE_SX}
        />

        <Stack flex={1}>
          <Tooltip title={hasEllipsis ? title : undefined} placement="top">
            <ListItemName ref={ref}>{title}</ListItemName>
          </Tooltip>

          <Stack direction="row" alignItems="center" sx={{ gap: '0.25rem' }}>
            {!!exposureType && (
              <ShoppingCartSubtitle body={exposureType} hasDivider={!!floor || !!placementArea} />
            )}

            {!!floor && <ShoppingCartSubtitle body={floor} hasDivider={!!placementArea} />}

            {!!placementArea && <ShoppingCartSubtitle body={placementArea} />}
          </Stack>
        </Stack>
      </Stack>

      <Stack alignItems="flex-end" justifyContent="center">
        <Typography sx={{ fontSize: '0.75rem', color: 'accent.main' }}>
          <PriceText price={price} />
        </Typography>

        {isHovered ? (
          <Button
            startIcon={<DeleteOutlinedIcon sx={{ width: '0.625rem', height: '0.625rem' }} />}
            color="error"
            onClick={() => removeSelectedRow(table, rowId)}
            sx={{
              fontSize: '0.5rem',
              padding: '0.125rem 0.25rem',
              borderRadius: '0.125rem',
              gap: '0.1rem',

              '&:hover': {
                backgroundColor: 'red.100',
              },
            }}
          >
            {t('Remove')}
          </Button>
        ) : (
          <Box
            sx={{ width: '3.25rem', height: '1.05rem', padding: '0!important', display: 'block' }}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default ShoppingCartListItem;
